import {
  getSensorInstallState,
  getSensorPillState,
  Sensor,
  SensorAlert,
  SensorAlertSeverity,
} from "common";
import { useTranslation } from "react-i18next";

import { StatusPillProps } from "../components/StatusPill";

export const useStatusPillProps = () => {
  const { t } = useTranslation();

  const generateAlertStatusPillProps = (
    alert: SensorAlert | null | undefined,
  ): Pick<
    StatusPillProps,
    "outlined" | "error" | "info" | "success" | "warning" | "children"
  > => {
    if (alert?.severity === SensorAlertSeverity.Critical) {
      return {
        children: t("alerts.severity.critical"),
        outlined: false,
        error: true,
        info: false,
        success: false,
        warning: false,
      };
    }

    if (alert?.severity === SensorAlertSeverity.High) {
      return {
        children: t("alerts.severity.high"),
        outlined: false,
        error: true,
        info: false,
        success: false,
        warning: false,
      };
    }

    if (alert?.severity === SensorAlertSeverity.Medium) {
      return {
        children: t("alerts.severity.medium"),
        outlined: false,
        error: false,
        info: false,
        success: false,
        warning: true,
      };
    }

    if (alert?.severity === SensorAlertSeverity.Low) {
      return {
        children: t("alerts.severity.low"),
        outlined: false,
        error: false,
        info: false,
        success: false,
        warning: true,
      };
    }

    return {
      children: t("alerts.severity.info"),
      outlined: false,
      error: false,
      info: true,
      success: false,
      warning: false,
    };
  };

  const generateSensorStatusPillProps = (
    sensor: Sensor | undefined,
    alert?: SensorAlert,
  ): Pick<
    StatusPillProps,
    "outlined" | "error" | "success" | "warning" | "waiting" | "children"
  > => {
    if (
      alert?.severity === SensorAlertSeverity.Critical ||
      alert?.severity === SensorAlertSeverity.High
    ) {
      return {
        children: t("sensors.statusPill.criticalAlert"),
        outlined: false,
        error: true,
        success: false,
        warning: false,
      };
    }

    if (
      alert?.severity === SensorAlertSeverity.Medium ||
      alert?.severity === SensorAlertSeverity.Low
    ) {
      return {
        children: t("sensors.statusPill.mediumAlert"),
        outlined: false,
        error: false,
        success: false,
        warning: true,
      };
    }

    // (A third branch re-testing `Medium || Low` used to sit here, rendering
    // `sensors.statusPill.lowAlert`. The block above already returned for both
    // severities, so it was unreachable — deleted rather than left to look
    // like Info was a state the pill could reach.)

    // Same inclusion-lifecycle helper mobile uses (SensorStatusPill) — keeps
    // Pairing/PairingFailed/PairedAwaitingData and the stale-Pending timeout
    // in sync between web-admin and mobile instead of a hand-rolled mapping.
    const pillState = sensor
      ? getSensorPillState(sensor, Math.floor(Date.now() / 1000))
      : "pending";

    switch (pillState) {
      case "active":
        return {
          children: t("sensors.statusPill.active"),
          outlined: true,
          error: false,
          success: true,
          warning: false,
        };

      // Gray, like mobile's SensorStatusPill: maintenance is a to-do, not a
      // hazard (Aidan, 2026-09-21).
      case "maintenance":
        return {
          children: t("sensors.statusPill.maintenance"),
          outlined: true,
          error: false,
          success: false,
          warning: false,
        };

      case "pending_stale":
        return {
          children: t("sensors.statusPill.pendingStale"),
          outlined: true,
          error: true,
          success: false,
          warning: false,
        };

      case "pairing_failed":
        return {
          children: t("sensors.statusPill.pairingFailed"),
          outlined: true,
          error: true,
          success: false,
          warning: false,
        };

      case "pairing":
        return {
          children: t("sensors.statusPill.pairing"),
          outlined: true,
          error: false,
          success: false,
          warning: false,
          waiting: true,
        };

      case "paired_awaiting_data":
        return {
          children: t("sensors.statusPill.pairedAwaitingData"),
          outlined: true,
          error: false,
          success: false,
          warning: false,
          waiting: true,
        };

      case "draft":
        return {
          children: t("sensors.statusPill.draft"),
          outlined: true,
          error: false,
          success: false,
          warning: false,
        };

      case "pending":
        return {
          children: t("sensors.statusPill.pending"),
          outlined: true,
          error: false,
          success: false,
          warning: false,
          waiting: true,
        };

      // "Inactive" hid two different problems with two different fixes, and
      // this hook had no split at all where mobile's SensorStatusPill did —
      // so "matching mobile" was coincidence, not structure. Installed-once
      // and now silent reads "Offline" (power / batteries / range);
      // never-paired reads "Never paired" (unfinished install work). Both are
      // neutral: neither is a hazard.
      case "inactive": {
        const installState = sensor
          ? getSensorInstallState(sensor, Math.floor(Date.now() / 1000))
          : "not_installed";
        return {
          children:
            installState === "offline"
              ? t("sensors.statusPill.offline")
              : t("sensors.statusPill.neverPaired"),
          outlined: true,
          error: false,
          success: false,
          warning: false,
        };
      }

      // Unfused from `case "inactive"` above. While they shared an arm, a new
      // SensorPillState would silently render as "Inactive"; now it renders
      // as its own unknown state instead of quietly claiming to be something
      // it isn't.
      default:
        return {
          children: t("sensors.statusPill.inactive"),
          outlined: true,
          error: false,
          success: false,
          warning: false,
        };
    }
  };

  return {
    generateAlertStatusPillProps,
    generateSensorStatusPillProps,
  };
};
